import React, { useEffect, useMemo, useState } from 'react';
import { AIResponseRenderer } from '../../handler/TextRenderer/LLMTextRenderer';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import TestView from '../Partials/TestView';
import CodeDiffViewer from './CodeDiffViewer';
import VisualizationRenderer from "../visualizations/VisualizationRenderer";
import PDFViewer from './PDFViewer';
import NativeDocViewer, {
  NATIVE_EXTENSIONS,
  getExtension,
} from './NativeDocViewer';
import { Resource } from '../../types/resource';
import { useWindow } from '../../contexts/WindowContext';
import { modernDarkTheme, modernLightTheme } from '../../helpers/codeHandler';
import { getSafeIframeSrcDocForCode } from './sandboxUtils';
import { getCodeLanguageByExtension } from '../../hooks/FileHandler/utils';

interface ContentDisplayViewProps {
  currentResourceVersion: Resource;
  chatId: string;
  showDiff: boolean;
  versions: Resource[];
  currentIndex: number;
  showPreview: boolean;
  /** Shared chats: nothing is written back (e.g. quiz answers). */
  readOnly?: boolean;
}

// Formats we render natively with our own in-app OpenXML viewers.
const NATIVE_DOC_EXTENSIONS: string[] = [...NATIVE_EXTENSIONS];
// Legacy binary / niche formats we can't parse in-browser. We surface a clean
// download-link fallback rather than embedding a third-party iframe viewer.
const UNSUPPORTED_DOC_EXTENSIONS = [
  '.doc', '.ppt', '.xls',
  '.rtf',
  '.odt', '.ods', '.odp',
  '.pages',
  '.abw',
];

/**
 * Lazily fetches a text file body from `url` when the in-memory content is
 * empty -- happens for attachment resources that were rehydrated from the
 * message store without inline content (we never persist raw file bodies).
 */
const useLazyTextContent = (initialContent: string, url: string | undefined) => {
  const [fetched, setFetched] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (initialContent || !url) {
      setFetched(null);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(url)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then(text => {
        if (!cancelled) {
          setFetched(text);
          setLoading(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err?.message || 'Failed to load file');
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [initialContent, url]);

  return {
    content: initialContent || fetched || '',
    loading,
    error,
    hasRemoteContent: !initialContent && fetched !== null,
  };
};

const CodePreviewIframe: React.FC<{ content: string; language: string }> = React.memo(
  ({ content, language }) => {
    const srcDoc = useMemo(
      () => getSafeIframeSrcDocForCode(content, language),
      [content, language],
    );
    return (
      <iframe
        srcDoc={srcDoc}
        sandbox="allow-scripts allow-forms allow-popups allow-modals allow-pointer-lock"
        style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
        title="Executable Content Preview"
      />
    );
  },
);
CodePreviewIframe.displayName = 'CodePreviewIframe';

const ContentDisplayView: React.FC<ContentDisplayViewProps> = ({
  currentResourceVersion,
  chatId,
  showDiff,
  versions,
  currentIndex,
  showPreview,
  readOnly = false,
}) => {
  const { resolvedTheme } = useWindow();

  const isCode = currentResourceVersion?.type === 'code';
  const isPdf = currentResourceVersion?.type === 'pdf';
  const isTextFile = currentResourceVersion?.type === 'doc';
  const isQuiz = currentResourceVersion?.type === 'quiz';

  const resourceExt = currentResourceVersion
    ? getExtension(currentResourceVersion.title, currentResourceVersion.url || '')
    : '';
  const isNativeDoc = isTextFile && NATIVE_DOC_EXTENSIONS.includes(resourceExt);
  const isUnsupportedDoc = isTextFile && UNSUPPORTED_DOC_EXTENSIONS.includes(resourceExt);
  // Only fetch-as-text for code files and text-like docs; never for binary
  // formats (fetching those as text would show garbage).
  const shouldLazyFetch =
    (isCode || (isTextFile && !isNativeDoc && !isUnsupportedDoc)) &&
    !currentResourceVersion?.content;

  const { content: resolvedContent, loading, error } = useLazyTextContent(
    shouldLazyFetch ? '' : currentResourceVersion?.content || '',
    shouldLazyFetch ? currentResourceVersion?.url : undefined,
  );

  if (!currentResourceVersion) return null;

  if (currentResourceVersion.type === 'visualization') {
    return <VisualizationRenderer content={currentResourceVersion.content} />;
  }

  if (isPdf) {
    return (
      <PDFViewer
        content={currentResourceVersion.content}
        url={currentResourceVersion.url}
      />
    );
  }

  if (isTextFile) {
    // Modern OpenXML (docx/pptx/xlsx) -- render natively in-app.
    if (isNativeDoc && currentResourceVersion.url) {
      return (
        <NativeDocViewer
          url={currentResourceVersion.url}
          title={currentResourceVersion.title}
        />
      );
    }

    // Legacy binary or niche formats (.doc/.ppt/.xls/.rtf/.odt/...) -- we
    // can't parse these in-browser and prior cloud-viewer iframes were
    // fragile/CORS-broken, so surface a clean download link instead.
    if (isUnsupportedDoc) {
      return (
        <div className="text-file-fallback">
          <p>In-app preview isn't available for this file type.</p>
          {currentResourceVersion.url && (
            <a
              href={currentResourceVersion.url}
              target="_blank"
              rel="noopener noreferrer"
              className="pdf-link"
            >
              Download / open in a new tab
            </a>
          )}
        </div>
      );
    }

    if (loading) {
      return <div className="text-file-loading">Loading file…</div>;
    }

    if (error && !resolvedContent) {
      return (
        <div className="text-file-fallback">
          <p>Couldn't load this file.</p>
          {currentResourceVersion.url && (
            <a
              href={currentResourceVersion.url}
              target="_blank"
              rel="noopener noreferrer"
              className="pdf-link"
            >
              Open in a new tab
            </a>
          )}
        </div>
      );
    }

    return (
      <div className="text-file-content">
        <AIResponseRenderer content={resolvedContent} isGenerating={false} />
      </div>
    );
  }

  if (isCode) {
    if (loading) {
      return <div className="text-file-loading">Loading file…</div>;
    }

    if (error && !resolvedContent) {
      return (
        <div className="text-file-fallback">
          <p>Couldn't load this file.</p>
          {currentResourceVersion.url && (
            <a
              href={currentResourceVersion.url}
              target="_blank"
              rel="noopener noreferrer"
              className="pdf-link"
            >
              Open in a new tab
            </a>
          )}
        </div>
      );
    }

    // For code attachments the resource's `language` may be unset when it was
    // rehydrated from storage; derive from the filename as a fallback.
    const language =
      currentResourceVersion.language ||
      (currentResourceVersion.title ? getCodeLanguageByExtension(currentResourceVersion.title) : 'text');

    if (showPreview) {
      return (
        <CodePreviewIframe content={resolvedContent} language={language} />
      );
    }

    if (showDiff && currentIndex > 0) {
      return (
        <CodeDiffViewer
          previousContent={versions[currentIndex - 1]?.content || ''}
          currentContent={resolvedContent}
        />
      );
    }
    return (
      <div
        style={{
          width: '100%',
          minHeight: '100%',
          height: 'auto',
          overflow: 'auto',
          background: resolvedTheme === 'dark'
            ? modernDarkTheme['pre[class*="language-"]']?.background || '#181a1b'
            : modernLightTheme['pre[class*="language-"]']?.background || '#f5f5f5',
        }}
      >
        <SyntaxHighlighter
          language={language}
          wrapLines={true}
          wrapLongLines={true}
          style={resolvedTheme === 'dark' ? modernDarkTheme as any : modernLightTheme as any}
          customStyle={{
            margin: 0,
            borderRadius: '0',
            padding: 'var(--spacing-sm) var(--spacing-md)',
            width: '100%',
            fontSize: 'var(--font-size-xs)',
            border: 'none',
            minHeight: '100%',
            height: 'auto',
            boxSizing: 'border-box',
            background: 'transparent',
          }}
          codeTagProps={{
            style: {
              tabSize: 1,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              display: 'block',
              minHeight: '100%',
              height: 'auto',
              background: 'transparent',
            }
          }}
        >
          {resolvedContent}
        </SyntaxHighlighter>
      </div>
    );
  }

  if (isQuiz) {
    // Keyed so switching quizzes starts from that quiz's saved answers.
    return <TestView key={currentResourceVersion.id} resource={currentResourceVersion} chatId={chatId} readOnly={readOnly} />;
  }

  return (
    <div className="essay-text">
      <AIResponseRenderer content={currentResourceVersion.content} isGenerating={false} />
    </div>
  );
};

export default ContentDisplayView;
